import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { SupportThreadService } from 'app/services/dal/support-thread.service';
import { SupportMessageService } from 'app/services/dal/support-message.service';
import { SupportAttachmentService } from 'app/services/dal/support-attachment.service';
import { ToastService } from 'app/services/toast.service';
import { AuthService } from 'app/services/auth.service';
import * as fromUser from 'app/state/user/reducer';
import * as fromBar from 'app/state/bar';
import * as fromSupport from './reducer';
import * as Actions$ from './actions';
import { shouldLoadDataSimple, trackLoadingSimple } from '../effect-helpers';
import { ISupportThread, ISupportMessage } from 'app/models/support';
import { detectPlatform, messagePreview, stripHtml } from 'app/helpers/support.helpers';
import { isSupportTeamMember } from 'app/helpers/support-team';
import packageJson from '../../../../package.json';

@Injectable()
export class SupportEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private threads = inject(SupportThreadService);
  private messages = inject(SupportMessageService);
  private attachments = inject(SupportAttachmentService);
  private toast = inject(ToastService);
  private auth = inject(AuthService);

  loadUserThreadsFromState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Actions$.loadUserThreadsFromState),
      shouldLoadDataSimple(this.store, 'supportThreads', fromSupport.selectAllThreads),
      switchMap((action) =>
        this.store.select(fromUser.selectUser).pipe(
          filter((user): user is NonNullable<typeof user> => !!user?.uid),
          take(1),
          switchMap((user) => {
            const isDev = isSupportTeamMember(user.email);
            const obs = isDev
              ? this.threads.getAllThreadsForDev(action.take ?? 200)
              : this.threads.getThreadsForUser(user.uid!, action.take ?? 100);
            return trackLoadingSimple(
              this.store,
              'supportThreads',
              obs.pipe(
                map((items) => Actions$.loadUserThreadsSuccess({ items })),
                catchError((error) => of(Actions$.loadUserThreadsFailure({ error }))),
              ),
            );
          }),
        ),
      ),
    ),
  );

  loadThread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Actions$.loadThread),
      switchMap(({ id }) =>
        this.threads.getThread(id).pipe(
          filter((item): item is ISupportThread => !!item && !!item.id),
          map((item) => Actions$.loadThreadSuccess({ item })),
          catchError((error) => of(Actions$.loadThreadFailure({ error }))),
        ),
      ),
    ),
  );

  addThread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Actions$.addThread),
      withLatestFrom(
        this.store.select(fromUser.selectUser),
        this.store.select(fromBar.selectBarId),
      ),
      mergeMap(([action, user, barId]) => {
        if (!user?.uid) {
          return of(Actions$.addThreadFailure({ error: 'No authenticated user' }));
        }
        // If an existing draft is sent, the document is reused (status flips
        // to 'open'); otherwise the thread is created as before. Only WHEN the
        // first message is written are the developers notified (onSupportMessageCreated).
        const id = action.draftId ?? this.threads.getNewId();
        const now = new Date();
        return from(
          (async () => {
            const newlyUploaded = action.files.length
              ? await this.attachments.uploadMany(user.uid!, id, action.files)
              : [];
            const uploaded = [...(action.draftAttachments ?? []), ...newlyUploaded];
            const preview = messagePreview(action.description);
            const thread: ISupportThread = {
              id,
              barId: barId ?? '',
              userId: user.uid!,
              userEmail: user.email ?? '',
              userDisplayName: user.displayName ?? user.email ?? 'Unknown',
              title: action.title,
              description: action.description,
              appVersion: (packageJson as any).version,
              platform: detectPlatform(),
              attachments: uploaded,
              lastMessageAt: now,
              lastMessageBy: 'user',
              lastMessagePreview: preview,
              unreadByUser: false,
              unreadByDev: true,
              status: 'open',
              createdAt: now,
              updatedAt: now,
            };
            if (action.draftId) {
              const { id: _omit, createdAt: _keep, ...patch } = thread;
              await this.threads.updateThread(action.draftId, patch, user.uid!);
            } else {
              await this.threads.addThread(thread);
            }
            // Also write the initial description as the first message so the chat view shows it
            const firstMessage: ISupportMessage = {
              threadId: id,
              body: action.description,
              authorId: user.uid!,
              authorEmail: user.email ?? '',
              authorDisplayName: user.displayName ?? user.email ?? 'Unknown',
              authorRole: 'user',
              attachments: uploaded,
              internal: false,
              createdAt: now,
            };
            await this.messages.addMessage(id, firstMessage);
            return thread;
          })(),
        ).pipe(
          map((item) => Actions$.addThreadSuccess({ item })),
          catchError((error) => of(Actions$.addThreadFailure({ error }))),
        );
      }),
    ),
  );

  addThreadSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Actions$.addThreadSuccess),
        tap(() => this.toast.toastSuccess('Support request created')),
      ),
    { dispatch: false },
  );

  addThreadFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Actions$.addThreadFailure),
        tap(({ error }) => this.toast.error('Could not create support request: ' + JSON.stringify(error))),
      ),
    { dispatch: false },
  );

  assignToMe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Actions$.assignToMe),
      withLatestFrom(this.store.select(fromUser.selectUser)),
      mergeMap(([action, user]) => {
        if (!user?.uid) {
          return of(Actions$.assignToMeFailure({ error: 'No user' }));
        }
        return from(this.threads.assignTo(action.id, user.uid, user.email ?? '')).pipe(
          map(() =>
            Actions$.assignToMeSuccess({ id: action.id, uid: user.uid!, email: user.email ?? '' }),
          ),
          catchError((error) => of(Actions$.assignToMeFailure({ error }))),
        );
      }),
    ),
  );

  archiveThread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Actions$.archiveThread),
      mergeMap(({ id, archived }) =>
        from(this.threads.archiveThread(id, archived)).pipe(
          map(() => Actions$.archiveThreadSuccess({ id, archived })),
          catchError((error) => of(Actions$.archiveThreadFailure({ error }))),
        ),
      ),
    ),
  );

  archiveThreadSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Actions$.archiveThreadSuccess),
        tap(({ archived }) => this.toast.toastSuccess(archived ? 'Thread archived' : 'Thread unarchived')),
      ),
    { dispatch: false },
  );

  archiveThreadFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Actions$.archiveThreadFailure),
        tap(({ error }) => this.toast.error('Could not archive thread: ' + JSON.stringify(error))),
      ),
    { dispatch: false },
  );

  markRead$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Actions$.markThreadRead),
        tap((action) => {
          this.threads.markRead(action.id, action.role);
        }),
      ),
    { dispatch: false },
  );

  loadMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Actions$.loadMessages),
      switchMap(({ threadId }) =>
        this.store.select(fromUser.selectUser).pipe(
          take(1),
          switchMap((user) => {
            const isDev = isSupportTeamMember(user?.email);
            return this.messages.getMessages(threadId, isDev).pipe(
              map((items) => Actions$.loadMessagesSuccess({ threadId, items })),
              catchError((error) => of(Actions$.loadMessagesFailure({ error }))),
            );
          }),
        ),
      ),
    ),
  );

  addMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Actions$.addMessage),
      withLatestFrom(
        this.store.select(fromUser.selectUser),
        this.store.select(fromSupport.selectThreadEntities),
      ),
      mergeMap(([action, user, entities]) => {
        if (!user?.uid) {
          return of(Actions$.addMessageFailure({ error: 'No user' }));
        }
        const isDev = isSupportTeamMember(user.email);
        const threadOwnerUid = entities[action.threadId]?.userId ?? user.uid!;
        return from(
          (async () => {
            const uploaded = action.files.length
              ? await this.attachments.uploadMany(threadOwnerUid, action.threadId, action.files)
              : [];
            const now = new Date();
            const internal = !!action.internal && isDev;
            const msg: ISupportMessage = {
              threadId: action.threadId,
              body: action.body,
              authorId: user.uid!,
              authorEmail: user.email ?? '',
              authorDisplayName: user.displayName ?? user.email ?? 'Unknown',
              authorRole: isDev ? 'developer' : 'user',
              attachments: uploaded,
              internal,
              createdAt: now,
            };
            const item = await this.messages.addMessage(action.threadId, msg);

            let threadChanges: Partial<ISupportThread> | undefined;
            if (!internal) {
              const previewText = stripHtml(action.body);
              const preview = previewText.length > 120
                ? previewText.substring(0, 120) + '...'
                : previewText;
              threadChanges = {
                lastMessageAt: now,
                lastMessageBy: isDev ? 'developer' : 'user',
                lastMessagePreview: preview,
                unreadByDev: !isDev,
                unreadByUser: isDev,
                updatedAt: now,
              };
              await this.threads.applyReplyUpdate(action.threadId, threadChanges, user.uid!);
            }

            return { item, threadChanges };
          })(),
        ).pipe(
          map(({ item, threadChanges }) =>
            Actions$.addMessageSuccess({ threadId: action.threadId, item, threadChanges }),
          ),
          catchError((error) => of(Actions$.addMessageFailure({ error }))),
        );
      }),
    ),
  );

  addMessageFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Actions$.addMessageFailure),
        tap(({ error }) => this.toast.error('Could not send message: ' + JSON.stringify(error))),
      ),
    { dispatch: false },
  );

  deleteThread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Actions$.deleteThread),
      mergeMap(({ id }) =>
        from(this.threads.deleteThread(id)).pipe(
          map(() => Actions$.deleteThreadSuccess({ id })),
          catchError((error) => of(Actions$.deleteThreadFailure({ error }))),
        ),
      ),
    ),
  );

  deleteThreadSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Actions$.deleteThreadSuccess),
        tap(() => this.toast.toastSuccess('Support request deleted')),
      ),
    { dispatch: false },
  );

  deleteThreadFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Actions$.deleteThreadFailure),
        tap(({ error }) => this.toast.error('Could not delete support request: ' + JSON.stringify(error))),
      ),
    { dispatch: false },
  );

  deleteMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Actions$.deleteMessage),
      mergeMap(({ threadId, id }) =>
        from(this.messages.deleteMessage(threadId, id)).pipe(
          map(() => Actions$.deleteMessageSuccess({ threadId, id })),
          catchError((error) => of(Actions$.deleteMessageFailure({ error }))),
        ),
      ),
    ),
  );

  deleteMessageFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Actions$.deleteMessageFailure),
        tap(({ error }) => this.toast.error('Could not delete message: ' + JSON.stringify(error))),
      ),
    { dispatch: false },
  );
}
